// https://github.com/mui-org/material-ui/blob/master/docs/src/pages/demos/paper/PaperSheet.js

import React from 'react';
import PropTypes from 'prop-types';
import MuiPaper from '@material-ui/core/Paper';
import Clickable from '../Clickable';
import styled from 'styled-components/macro';
import withStatus from '../../contexts/withStatus';
import ThemeProvider from '../../components/ThemeProvider';
import getThemeValue from '../../utils/getThemeValue';
import isNumber from 'is-number';
import { connect } from 'react-redux';
import classNames from 'classnames';

const FilterPaperProps = ({
  backgroundColor,
  color,
  corners,
  fullHeight,
  inline,
  marginBottom,
  overflow,
  padding,
  position,
  width,
  image,
  assets,
  imageSize,
  ...rest
}) => {
  if (position && inline) {
    console.error('Warning: `position` and `inline` props are not compatible.');
  }

  return <MuiPaper {...rest} />;
};
// dotProp.get(theme, backgroundColor) ||

const getBackground = ({ theme, backgroundColor }) =>
  getThemeValue(theme, backgroundColor) ||
  backgroundColor ||
  theme.palette.background.paper ||
  null;

const getPadding = (theme, padding = '3') =>
  padding
    .toString()
    .trim()
    .split(' ')
    .map(p => `${(isNumber(p) ? +p : 3) * theme.spacing(1)}px`)
    .join(' ');

const StyledPaper = styled(FilterPaperProps)`
  && {
    position: relative;

    /* overflow defaults to hidden, keeps ripples inside round corner */
    overflow: ${({ overflow }) => overflow || 'hidden'};

    margin: ${({ position }) =>
      position === 'center'
        ? '0 auto'
        : position === 'right'
        ? '0 0 0 auto'
        : 0};

    /* configurations */
    margin-bottom: ${({ theme, marginBottom, fullHeight }) =>
      fullHeight
        ? 0
        : (isNumber(marginBottom) ? marginBottom : 2) * theme.spacing(1)}px;

    background-color: ${props => getBackground(props)};
    background-image: ${({ assets, image }) => `url(${assets[image]})`};
    background-size: ${({ imageSize }) => imageSize || 'cover'};
    background-repeat: no-repeat;
    background-position: 50% 50%;

    border-radius: ${({ theme, corners }) =>
      isNumber(corners)
        ? corners + 'px'
        : corners === 'square'
        ? '0px'
        : corners === 'circle'
        ? '500px'
        : theme.shape.borderRadius};

    color: ${({ color, theme }) => getThemeValue(theme, color) || color};
    ${({ fullHeight }) => fullHeight && { height: '100%' }}
    ${({ inline }) => inline && 'display: inline-block;'}
    ${({ width }) => width && `width: ${width}px;`}
    max-width: 100%;
    padding: ${({ theme, padding }) => getPadding(theme, padding)};

    /* if we have actions, add hover state */
    ${({ actions }) =>
      actions &&
      `
        transition: opacity 0.15s linear;

        &:hover {
          opacity: 0.7;
          transition: opacity 0.3s linear;
        }
      `}
  }
`;

function Paper(props) {
  const {
    children,
    actions,
    elevation,
    className,
    status,
    id,
    theme,
    ...rest
  } = props;

  const Component = (
    <StyledPaper
      elevation={elevation || 0}
      className={classNames('paper', className)}
      actions={actions}
      {...rest}
    >
      {children}
      {actions && (
        <Clickable
          actions={actions}
          status={status}
          id={id}
          disabled={status === 'locked'}
        />
      )}
    </StyledPaper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{Component}</ThemeProvider>
  ) : (
    Component
  );
}

const backgroundColorValidator = (props, propName, componentName) => {
  const value = props[propName];

  // only validate if a string
  if (typeof value !== 'string') {
    return;
  }

  // can be a dot-prop address in the theme object or a valid color string
  const isValid =
    value === 'transparent' ||
    /^palette\./.test(value) ||
    /^#/.test(value) ||
    /^rgb/.test(value) ||
    /^hsl/.test(value);

  // return error if not valid
  if (!isValid) {
    return new Error(
      `Invalid string \`${propName}\` supplied to backgroundColor prop of \`${componentName}\`. String must begin with  "palette.", "#", "rgb", or "hsl".`
    );
  }
};

Paper.propTypes = {
  // required
  children: PropTypes.node.isRequired,

  // optional
  id: PropTypes.string,
  theme: PropTypes.string,
  elevation: PropTypes.number,
  actions: PropTypes.arrayOf(PropTypes.object),
  backgroundColor: backgroundColorValidator,
  marginBottom: PropTypes.number,
  borderRadius: PropTypes.number
};

export default connect(state => ({ assets: state.content.assets }))(
  withStatus(Paper)
);
